/**
 * Inline stroke icons for the guest signing view, traced 1:1 from the
 * "Signing View" frame of `Signer Experience.dc.html`. MUI's icon set is
 * filled-glyph based and cannot reproduce the design's 1.9–2.6px hairlines, so
 * this screen draws its own. Every icon inherits `currentColor` and takes its
 * box from `size`, matching each design usage exactly.
 */
import React from "react";

export interface SignerIconProps {
  /** Square viewport in px — the design's per-usage icon size. */
  size?: number;
  className?: string;
}

const Svg = ({
  size = 16,
  strokeWidth,
  strokeLinecap = "round",
  strokeLinejoin = "round",
  children,
  className,
}: SignerIconProps & {
  strokeWidth: number;
  /** The design draws a few glyphs on the SVG defaults — match per icon. */
  strokeLinecap?: "round" | "butt";
  strokeLinejoin?: "round" | "miter";
  children: React.ReactNode;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap={strokeLinecap}
    strokeLinejoin={strokeLinejoin}
    className={className}
    aria-hidden="true"
    focusable="false"
    style={{ display: "block", flexShrink: 0 }}
  >
    {children}
  </svg>
);

/** Cross — modal close buttons. */
export const CloseIcon = ({ size = 16, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={2} {...rest}>
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </Svg>
);

/** Pencil — "Start signing" / "Review & sign". */
export const PencilIcon = ({ size = 15, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={2} {...rest}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </Svg>
);

/**
 * Chevrons — document stepper, page stepper and field navigator. Every one of
 * the design's 14 chevron/caret glyphs sets `stroke-linecap: round` but leaves
 * the join on the SVG default, so the vertex is mitred, not rounded.
 */
export const ChevronLeftIcon = ({ size = 11, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={2.4} strokeLinejoin="miter" {...rest}>
    <path d="M15 6l-6 6 6 6" />
  </Svg>
);

export const ChevronRightIcon = ({ size = 11, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={2.4} strokeLinejoin="miter" {...rest}>
    <path d="M9 6l6 6-6 6" />
  </Svg>
);

/** Chevron used by the field navigator's "Next" pill (heavier stroke). */
export const NextChevronIcon = ({ size = 12, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={2.6} strokeLinejoin="miter" {...rest}>
    <path d="M9 6l6 6-6 6" />
  </Svg>
);

/** Caret — document picker button and menus. */
export const CaretDownIcon = ({ size = 11, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={2.5} strokeLinejoin="miter" {...rest}>
    <path d="M6 9l6 6 6-6" />
  </Svg>
);

/** Document sheet — panel cards and the Documents rail tab. */
export const FileIcon = ({ size = 16, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={2} {...rest}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </Svg>
);

/** Sparkles — AI Assistant rail tab. */
export const SparkleIcon = ({ size = 17, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={1.9} {...rest}>
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
    <path d="M18.5 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z" />
  </Svg>
);

/** Trend line — AI Insights rail tab. */
export const InsightsIcon = ({ size = 17, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={2} {...rest}>
    <path d="M3 17l5-5 4 4 8-8" />
    <path d="M15 8h5v5" />
  </Svg>
);

/** Tick — completed document rows in the picker menu. */
export const CheckIcon = ({ size = 12, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={3} {...rest}>
    <path d="M5 13l4 4L19 7" />
  </Svg>
);

/** Eye — "Review only · no fields for you". */
export const EyeIcon = ({ size = 12, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={2} {...rest}>
    <circle cx="12" cy="12" r="3" />
    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
  </Svg>
);

/** Tick drawn inside a highlight swatch (heavier stroke than the row tick). */
export const SwatchCheckIcon = ({ size = 15, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={3.4} strokeLinejoin="miter" {...rest}>
    <path d="M5 13l4 4L19 7" />
  </Svg>
);

/** Arrow left — decline view's back button. */
export const ArrowLeftIcon = ({ size = 18, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={2} {...rest}>
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </Svg>
);

/** Highlighter — flag-count summary on the decline rail. */
export const HighlighterIcon = ({ size = 15, ...rest }: SignerIconProps) => (
  <Svg
    size={size}
    strokeWidth={2}
    strokeLinecap="butt"
    strokeLinejoin="miter"
    {...rest}
  >
    <path d="M4 21v-4l11-11 4 4L8 21H4z" />
    <path d="M13 6l4 4" />
  </Svg>
);

/**
 * Alert circle — decline consequence callout. The design leaves the SVG
 * defaults on this one, so the `M12 8h.01` dot renders butt-capped (i.e. as a
 * bare circle + bar); matched deliberately rather than "fixed" to round.
 */
export const AlertCircleIcon = ({ size = 14, ...rest }: SignerIconProps) => (
  <Svg
    size={size}
    strokeWidth={2}
    strokeLinecap="butt"
    strokeLinejoin="miter"
    {...rest}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8h.01" />
    <path d="M12 11v5" />
  </Svg>
);

/** Trash — "Clear" on the signature pad's draw canvas. */
export const TrashIcon = ({ size = 13, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={2} {...rest}>
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
  </Svg>
);

/** Upload cloud — the signature pad's drop zone. */
export const UploadCloudIcon = ({ size = 38, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={1.6} {...rest}>
    <path d="M17.5 18a4.5 4.5 0 0 0 .9-8.9A7 7 0 0 0 5 11.5 3.75 3.75 0 0 0 6.5 18z" />
    <path d="M9.5 13.5l2.5-2.5 2.5 2.5" />
    <path d="M12 11v6" />
  </Svg>
);

/** Padlock — locked (plan-gated) rail tabs. */
export const LockIcon = ({ size = 10, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={2.2} {...rest}>
    <rect x="4" y="11" width="16" height="9" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </Svg>
);

export const PaperclipIcon = ({ size = 14, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={1.8} {...rest}>
    <path d="M20.9 12.4L12.25 21.05a5.36 5.36 0 0 1-7.58-7.58l8.65-8.65a3.57 3.57 0 0 1 5.05 5.05l-8.66 8.65a1.79 1.79 0 0 1-2.53-2.53l7.99-7.98" />
  </Svg>
);

export const DownloadIcon = ({ size = 14, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={2} {...rest}>
    <path d="M12 3v12" />
    <path d="M7.5 10.5L12 15l4.5-4.5" />
    <path d="M4 19h16" />
  </Svg>
);

export const FilePdfIcon = ({ size = 17, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={1.7} {...rest}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M9 13h1.5a1.25 1.25 0 0 1 0 2.5H9V13v4" />
    <path d="M14 17v-4h1.6" />
    <path d="M14 15h1.4" />
  </Svg>
);

export const FileImageIcon = ({ size = 17, ...rest }: SignerIconProps) => (
  <Svg size={size} strokeWidth={1.7} {...rest}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <circle cx="10" cy="13" r="1.1" />
    <path d="M7 18l3-3 2 2 2.5-2.5L17 17" />
  </Svg>
);
